import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function RoleChips({ all, selected, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2">
      {all.map((r) => {
        const on = selected.includes(r.key);
        return (
          <button key={r.key} type="button" onClick={() => onToggle(r.key)}
            className={`px-2.5 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${on ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-[hsl(var(--accent))]" : "border-foreground/30 hover:border-foreground"}`}>
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AdminTeam() {
  const [members, setMembers] = useState([]);
  const [catalog, setCatalog] = useState({ roles: [], permissions: [] });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "", admin_roles: [] });
  const [editId, setEditId] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [m, c] = await Promise.all([
      api.get("/admin/team").then((r) => r.data),
      api.get("/admin/roles").then((r) => r.data),
    ]);
    setMembers(m); setCatalog(c);
  };
  useEffect(() => { load(); }, []);

  const label = (k) => catalog.roles.find((r) => r.key === k)?.label || k;
  const toggle = (list, k) => (list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) return toast.error("Name, email and password are required.");
    setBusy(true);
    try {
      await api.post("/admin/team", form);
      toast.success("Team member added");
      setAdding(false); setForm({ name: "", email: "", password: "", admin_roles: [] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add member"); }
    finally { setBusy(false); }
  };
  const saveRoles = async (uid) => {
    setBusy(true);
    try { await api.patch(`/admin/team/${uid}/roles`, { admin_roles: editRoles }); toast.success("Roles updated"); setEditId(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">System</p>
          <h2 className="font-serif text-4xl tracking-tighter">Team & Permissions.</h2>
        </div>
        {!adding && <button onClick={() => setAdding(true)} className="bg-foreground text-background px-5 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))]">+ Add member</button>}
      </div>

      {adding && (
        <div className="border border-foreground p-6 mb-8 max-w-xl space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="underline-input" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="underline-input" />
          </div>
          <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="Username (for login — unique)" autoCapitalize="none" className="underline-input w-full" />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password" className="underline-input w-full" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Roles</p>
            <RoleChips all={catalog.roles} selected={form.admin_roles} onToggle={(k) => setForm({ ...form, admin_roles: toggle(form.admin_roles, k) })} />
          </div>
          <div className="flex gap-3">
            <button disabled={busy} onClick={create} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50">Add member</button>
            <button onClick={() => setAdding(false)} className="border border-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3 mb-12">
        {members.map((m) => (
          <div key={m.id} className="border border-foreground p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="font-serif text-xl">{m.name}</p>
                <p className="font-mono text-[11px] text-muted-foreground">{m.email}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(m.admin_roles || []).length === 0 ? <span className="font-mono text-[10px] text-muted-foreground">No roles</span>
                    : (m.admin_roles || []).map((r) => <span key={r} className="font-mono text-[9px] uppercase tracking-widest border border-foreground/30 px-1.5 py-0.5">{label(r)}</span>)}
                </div>
              </div>
              {editId === m.id ? (
                <div className="flex gap-2">
                  <button disabled={busy} onClick={() => saveRoles(m.id)} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-3 py-1.5 text-[10px] font-mono uppercase disabled:opacity-50">Save</button>
                  <button onClick={() => setEditId(null)} className="border border-foreground px-3 py-1.5 text-[10px] font-mono uppercase">Cancel</button>
                </div>
              ) : (
                <button onClick={() => { setEditId(m.id); setEditRoles(m.admin_roles || []); }} className="border border-foreground px-3 py-1.5 text-[10px] font-mono uppercase hover:bg-foreground hover:text-background">Edit roles</button>
              )}
            </div>
            {editId === m.id && (
              <div className="mt-4 pt-4 border-t border-foreground/15">
                <RoleChips all={catalog.roles} selected={editRoles} onToggle={(k) => setEditRoles(toggle(editRoles, k))} />
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] mb-4">Roles reference</p>
      <div className="grid md:grid-cols-2 gap-4">
        {catalog.roles.map((r) => (
          <div key={r.key} className="border border-foreground/25 p-4">
            <p className="font-serif text-lg">{r.label}</p>
            <p className="text-sm text-muted-foreground mt-1">{r.description}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {r.permissions.map((p) => <span key={p} className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground border border-foreground/20 px-1.5 py-0.5">{p.replace(/\./g, " · ")}</span>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
